/**
 * Real cashback, month by month (§9, §13.3).
 *
 * The fiscal-year headline answers "how am I doing this year?". It cannot
 * answer "is this month better than last?" — which is the question a monthly
 * bill actually prompts, and the one the user asked for. So the same ledger is
 * sliced three ways here, on purpose:
 *
 *   * a **bar per month**, because rhythm is a shape before it is a number;
 *   * a **table**, because the shape is not auditable and the numbers are —
 *     it is also the accessible reading of the chart, not an afterthought;
 *   * a **per-card expansion** inside each month, because "which card earned
 *     this?" is the immediate follow-up question every time.
 *
 * Two deliberate choices worth defending:
 *
 * **Empty months are drawn, not skipped.** A series that omits June turns "we
 * earned nothing in June" into "June never happened", and the chart then reads
 * as a steady run of income it never was.
 *
 * **Gross credits sit beside the net figure.** A month that earned ₹419.40 and
 * had ₹419.40 clawed back nets to zero — identical, on a bar chart, to a month
 * in which nothing happened at all. They are not the same month, so the readout
 * and the table say which one it was.
 */

import { useQuery } from "@tanstack/react-query";
import { CalendarRange, ChevronRight, Clock, Download, Filter, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, EmptyState, Panel } from "@/components/ui/primitives";
import {
  cardsApi,
  cashbackApi,
  type CashbackMonth,
  type MonthlyScope,
  type MonthlySummary,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import {
  formatFiscalYear,
  formatMoney,
  formatMoneyCompact,
  formatMonth,
  formatMonthShort,
} from "@/lib/format";

export function MonthlyBreakdown({
  fiscalYear,
  cardId: lockedCardId,
  pickerHidden = false,
}: {
  fiscalYear: string;
  /** Set on a card's own page: the breakdown shows that card only, no picker. */
  cardId?: string;
  /** The page has its own card picker and drives `cardId`; don't show a second. */
  pickerHidden?: boolean;
}) {
  const [scope, setScope] = useState<MonthlyScope>("fiscal_year");
  const [pickedCardId, setCardId] = useState<string | null>(null);
  const cardId = lockedCardId ?? pickedCardId;

  const { data: cards } = useQuery({
    queryKey: ["cards", false],
    queryFn: () => cardsApi.list(false),
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "cashback",
      "monthly",
      scope,
      scope === "fiscal_year" ? fiscalYear : "all",
      cardId ?? "all-cards",
    ],
    queryFn: () => cashbackApi.monthly(scope, fiscalYear, cardId),
  });

  return (
    <Panel className="overflow-hidden">
      <header className="flex flex-wrap items-start justify-between gap-3 p-5 pb-4">
        <div>
          <h2 className="font-display text-base font-medium text-ink">Month by month</h2>
          <p className="mt-1 max-w-prose text-sm text-muted">
            {lockedCardId
              ? "This card's real cashback, split by the month the credit landed."
              : "The same money as the total above, split by the month the credit landed."}
            {scope === "fiscal_year"
              ? " These months add up to the year exactly."
              : " Every month on record, across fiscal years."}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="flex rounded-lg border border-hairline p-0.5"
            role="group"
            aria-label="Period"
          >
            <ScopeTab active={scope === "fiscal_year"} onClick={() => setScope("fiscal_year")}>
              {formatFiscalYear(fiscalYear)}
            </ScopeTab>
            <ScopeTab active={scope === "lifetime"} onClick={() => setScope("lifetime")}>
              All time
            </ScopeTab>
          </div>
          <Button asChild variant="default" size="sm">
            <a href={cashbackApi.monthlyExportUrl(scope, fiscalYear, cardId)} download>
              <Download className="h-4 w-4" aria-hidden />
              CSV
            </a>
          </Button>
        </div>
      </header>

      {/* Read across the wallet, or down one card. Single-select rather than
          the Transactions page's multi-select: the chart is one series, and
          two cards summed into one bar answers neither question. */}
      {!lockedCardId && !pickerHidden && (cards?.length ?? 0) > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 px-5 pb-4">
          <Filter className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
          <CardPill active={cardId === null} onClick={() => setCardId(null)}>
            All cards
          </CardPill>
          {cards!.map((c) => (
            <CardPill
              key={c.id}
              active={cardId === c.id}
              onClick={() => setCardId(cardId === c.id ? null : c.id)}
            >
              {c.nickname || c.product_name || c.issuer}
            </CardPill>
          ))}
        </div>
      )}

      {isError ? (
        <div className="flex items-center justify-between gap-3 border-t border-hairline px-5 py-4">
          <p className="text-sm text-muted">
            Couldn&rsquo;t load the monthly breakdown. {(error as Error).message}
          </p>
          <Button variant="default" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : isLoading || !data ? (
        <div className="space-y-3 border-t border-hairline p-5">
          <div className="skeleton h-14 w-full" />
          <div className="skeleton h-40 w-full" />
          <div className="skeleton h-9 w-full" />
        </div>
      ) : data.months.length === 0 ? (
        <EmptyState
          icon={CalendarRange}
          title="No months to show yet"
          body="Once a statement is parsed and a credit matches one of your cashback rules, the month it landed in appears here."
        />
      ) : (
        <MonthlyBody data={data} />
      )}
    </Panel>
  );
}

function ScopeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-[0.4rem] px-3 py-1 text-xs transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
        active ? "bg-brass/10 text-brass" : "text-muted hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function CardPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
        active
          ? "border-brass/50 bg-brass/10 text-brass"
          : "border-hairline text-muted hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function MonthlyBody({ data }: { data: MonthlySummary }) {
  const months = data.months;
  // Default to the newest month: it is the one the user is living in, and the
  // one the "vs last month" figure beside it is about.
  const [selected, setSelected] = useState<string | null>(months[months.length - 1]?.month ?? null);
  const [hovered, setHovered] = useState<string | null>(null);

  const active =
    months.find((m) => m.month === (hovered ?? selected)) ?? months[months.length - 1];

  return (
    <>
      <StatStrip data={data} />
      {active && (
        <Chart
          months={months}
          activeMonth={active.month}
          selected={selected}
          onHover={setHovered}
          onSelect={(month) => setSelected((current) => (current === month ? null : month))}
        />
      )}
      {active && <Readout month={active} pinned={active.month === selected && !hovered} />}
      <MonthTable months={months} selected={selected} onSelect={setSelected} />
      {data.timing_sensitive && <TimingNote months={months} />}
    </>
  );
}

/**
 * Why some monthly rates are missing.
 *
 * Some issuers post one cashback credit per statement cycle, and it rewards the
 * cycle before it. Which ones is measured from the user's own statements, never
 * hardcoded (§2.1), and named here from the data at runtime. The money is
 * booked in the month the statement puts it in, because the statement is the
 * truth (§2.2); what is *not* done is divide it by the wrong month's spend and
 * call the result a rate.
 */
function TimingNote({ months }: { months: CashbackMonth[] }) {
  const lagged = new Map<string, string>();
  for (const month of months) {
    for (const card of month.cards) {
      if (card.credit_timing === "cycle_credit") {
        lagged.set(card.card_id, card.nickname || card.product_name || card.issuer);
      }
    }
  }
  const names = [...lagged.values()];
  if (names.length === 0) return null;

  return (
    <p className="flex items-start gap-2 border-t border-hairline px-5 py-3 text-xs text-faint">
      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn" aria-hidden />
      <span>
        {names.join(" and ")} pay{names.length === 1 ? "s" : ""} one cashback credit per
        statement cycle, for the cycle before it. The money is counted in the month the
        statement credited it — that&rsquo;s the bill it actually reduced — but a rate
        against <em>that</em> month&rsquo;s spend would pair a credit with spend that
        didn&rsquo;t earn it, so it is withheld rather than printed.
      </span>
    </p>
  );
}

/**
 * The four comparisons a month-wise view exists to make. Kept as plain figures
 * rather than a second chart — an average and a best month are single numbers,
 * and §9's rule that the headline means one thing applies to these too.
 */
function StatStrip({ data }: { data: MonthlySummary }) {
  const best = data.months.find((m) => m.month === data.best_month);
  // The sign comes off the string rather than through Number(): the magnitude
  // is formatted from the API's own digits, never round-tripped as a float.
  const change = data.latest_change;
  const direction = change === null ? 0 : change.startsWith("-") ? -1 : Number(change) > 0 ? 1 : 0;
  const magnitude = change === null ? "" : formatMoney(change.replace("-", ""));

  return (
    <dl className="grid grid-cols-2 gap-px border-y border-hairline bg-hairline sm:grid-cols-4">
      <Stat
        label={data.scope === "lifetime" ? "All time" : "This year"}
        value={formatMoney(data.total)}
        hint={`over ${data.month_count} month${data.month_count === 1 ? "" : "s"}`}
        tone="brass"
      />
      <Stat
        label="Average a month"
        value={formatMoney(data.average_per_month)}
        hint={
          data.average_per_active_month
            ? `${formatMoney(data.average_per_active_month)} in the ${data.active_months} that earned`
            : "no month has earned yet"
        }
      />
      <Stat
        label="Best month"
        value={best ? formatMoney(best.real_cashback) : "—"}
        hint={best ? formatMonth(best.month) : "nothing earned yet"}
      />
      <Stat
        label="Latest vs previous"
        value={
          change === null
            ? "—"
            : `${direction > 0 ? "+" : direction < 0 ? "−" : ""}${magnitude}`
        }
        hint={
          change === null
            ? "no earlier month to compare"
            : // A month still in progress is being compared against a finished
              // one. Saying so stops a normal mid-month figure reading as a fall.
              data.months[data.months.length - 1]?.month === currentMonth()
              ? "this month is still open"
              : direction === 0
                ? "level with the month before"
                : `${direction > 0 ? "more" : "less"} than the month before`
        }
        tone={change === null || direction === 0 ? "ink" : direction > 0 ? "paid" : "warn"}
      />
    </dl>
  );
}

function Stat({
  label,
  value,
  hint,
  tone = "ink",
}: {
  label: string;
  value: string;
  hint: string;
  tone?: "ink" | "brass" | "paid" | "warn";
}) {
  return (
    <div className="bg-panel px-5 py-4">
      <dt className="label-micro">{label}</dt>
      <dd
        className={cn(
          "mt-1.5 font-display text-lg font-semibold tabular-nums",
          tone === "brass" && "text-brass",
          tone === "paid" && "text-paid",
          tone === "warn" && "text-warn",
          tone === "ink" && "text-ink",
        )}
      >
        {value}
      </dd>
      <p className="mt-0.5 text-xs text-faint">{hint}</p>
    </div>
  );
}

/**
 * One bar per month, anchored to a zero baseline.
 *
 * No tooltip: the readout below the chart is a fixed slot instead. A floating
 * tooltip inside a horizontally-scrolling container gets clipped by that same
 * scroll container, and it would be unreachable on touch — a readout answers
 * hover, focus and tap identically.
 */
function Chart({
  months,
  activeMonth,
  selected,
  onHover,
  onSelect,
}: {
  months: CashbackMonth[];
  activeMonth: string;
  selected: string | null;
  onHover: (month: string | null) => void;
  onSelect: (month: string) => void;
}) {
  const { positiveShare, maxPositive, maxNegative, ceiling } = useMemo(() => {
    const values = months.map((m) => Number(m.real_cashback));
    const maxPositive = Math.max(0, ...values);
    const maxNegative = Math.max(0, ...values.map((v) => -v));
    const span = maxPositive + maxNegative || 1;
    return {
      maxPositive,
      maxNegative,
      // A chart with no clawback month gives all its height to the positives.
      positiveShare: (maxPositive / span) * 100,
      ceiling: maxPositive,
    };
  }, [months]);

  // Past about 18 columns the month names collide, so they thin out to every
  // third — the readout carries the exact month either way.
  const labelEvery = months.length > 18 ? 3 : 1;

  return (
    <div className="px-5 pb-1 pt-5">
      <div className="flex items-start gap-3">
        {/* A single reference tick. More gridlines than this would out-shout
            three months of bars on a chart this short. */}
        <div
          className="hidden w-16 shrink-0 flex-col justify-between text-right text-micro tabular-nums text-faint sm:flex"
          style={{ height: `${CHART_HEIGHT}px` }}
          aria-hidden
        >
          <span>{ceiling > 0 ? formatMoneyCompact(ceiling) : ""}</span>
          <span className="translate-y-1/2">₹0</span>
        </div>

        <div className="min-w-0 flex-1 overflow-x-auto pb-2">
          <div className="flex min-w-full items-end gap-1">
            {months.map((month, index) => {
              const value = Number(month.real_cashback);
              const isActive = month.month === activeMonth;
              const isSelected = month.month === selected;
              return (
                <button
                  key={month.month}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={`${formatMonth(month.month)}: ${formatMoney(month.real_cashback)} real cashback from ${month.entry_count} entr${month.entry_count === 1 ? "y" : "ies"}`}
                  onMouseEnter={() => onHover(month.month)}
                  onMouseLeave={() => onHover(null)}
                  onFocus={() => onHover(month.month)}
                  onBlur={() => onHover(null)}
                  onClick={() => onSelect(month.month)}
                  className={cn(
                    "group flex min-w-[1.75rem] flex-1 flex-col rounded-md px-0.5 pb-1 pt-0",
                    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
                    isActive ? "bg-raised/60" : "hover:bg-raised/40",
                  )}
                >
                  <span
                    className="flex w-full flex-col"
                    style={{ height: `${CHART_HEIGHT}px` }}
                  >
                    <span
                      className="flex w-full items-end"
                      style={{ height: `${positiveShare}%` }}
                    >
                      {value > 0 && (
                        <span
                          className={cn(
                            "mx-auto w-full max-w-[3.25rem] rounded-t-[4px] transition-colors",
                            isActive ? "bg-brass" : "bg-brass/70",
                          )}
                          style={{ height: `${Math.max((value / maxPositive) * 100, 1.5)}%` }}
                        />
                      )}
                      {/* A month that earned nothing still gets a mark on the
                          baseline. An absent bar reads as missing data. */}
                      {value === 0 && (
                        <span className="mx-auto h-0.5 w-full max-w-[3.25rem] rounded-sm bg-hairline" />
                      )}
                    </span>
                    <span className="h-px w-full shrink-0 bg-hairline-strong" />
                    <span
                      className="flex w-full items-start"
                      style={{ height: `${100 - positiveShare}%` }}
                    >
                      {value < 0 && (
                        <span
                          className="mx-auto w-full max-w-[3.25rem] rounded-b-[4px] bg-overdue/80"
                          style={{ height: `${Math.max((-value / maxNegative) * 100, 1.5)}%` }}
                        />
                      )}
                    </span>
                  </span>

                  {/* Each label slot keeps its height when it has nothing to
                      say, so bars stay on one baseline whatever is under them. */}
                  <span
                    className={cn(
                      "mt-2 block h-4 truncate text-center text-micro tabular-nums",
                      isActive ? "text-ink" : "text-faint",
                    )}
                  >
                    {index % labelEvery === 0 ? formatMonthShort(month.month) : ""}
                  </span>
                  {/* The year appears once per January and on the first column,
                      so a lifetime series stays readable without repeating
                      "2026" twelve times. */}
                  <span className="block h-4 text-center text-micro text-faint">
                    {index === 0 || month.month.endsWith("-01") ? month.month.slice(0, 4) : ""}
                  </span>
                  {/* Clawbacks are not encoded by colour alone: the marker is a
                      shape under the axis and the readout spells it out. */}
                  <span className="block h-4 text-center text-micro text-warn">
                    {Number(month.clawbacks) > 0 ? "↺" : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const CHART_HEIGHT = 160;

/**
 * The month the user is currently in, as "2026-07".
 *
 * It matters because that month is always incomplete — its statement hasn't
 * been issued yet — so a bar that looks like a collapse is usually just a month
 * that isn't over. Saying so is cheaper than explaining it later.
 */
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** The hovered, focused or pinned month, spelled out. */
function Readout({ month, pinned }: { month: CashbackMonth; pinned: boolean }) {
  const clawed = Number(month.clawbacks) > 0;
  const partial = month.month === currentMonth();

  return (
    <div className="mx-5 mb-5 rounded-lg border border-hairline bg-raised/40 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-ink">{formatMonth(month.month)}</span>
          <span className="text-xs text-faint">
            {formatFiscalYear(month.fiscal_year)}
            {pinned ? " · pinned" : ""}
          </span>
        </div>
        <span className="flex items-baseline gap-2">
          {partial && <span className="text-xs text-faint">so far</span>}
          <span className="font-display text-lg font-semibold tabular-nums text-brass">
            {formatMoney(month.real_cashback)}
          </span>
        </span>
      </div>

      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
        <span>
          {month.entry_count} credit{month.entry_count === 1 ? "" : "s"}
        </span>
        <span>
          on {formatMoney(month.eligible_spend)} eligible spend
          {month.effective_rate ? (
            <>
              {" · "}
              <span className="text-ink tabular-nums">{month.effective_rate}%</span>
              {month.timing_sensitive && <span className="text-warn">*</span>}
            </>
          ) : (
            month.timing_sensitive && (
              <>
                {" · "}
                <span className="text-warn">rate withheld — credited a cycle late</span>
              </>
            )
          )}
        </span>
        {clawed && (
          <span className="inline-flex items-center gap-1 text-warn">
            <Undo2 className="h-3 w-3" aria-hidden />
            {formatMoney(month.credits)} earned, {formatMoney(month.clawbacks)} taken back
          </span>
        )}
        {Number(month.realized_rewards) > 0 && (
          <span>{formatMoney(month.realized_rewards)} realized outside the bill</span>
        )}
        {partial && (
          <span className="text-faint">
            This month is still open — its statement hasn&rsquo;t been issued yet.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The auditable reading of the chart. Also the accessible one — a bar has a
 * height, a row has a number, and only one of those can be read aloud.
 */
function MonthTable({
  months,
  selected,
  onSelect,
}: {
  months: CashbackMonth[];
  selected: string | null;
  onSelect: (month: string | null) => void;
}) {
  // Newest first: the interesting month is almost always the recent one, and a
  // long lifetime list would otherwise open on a year the user has forgotten.
  const rows = useMemo(() => [...months].reverse(), [months]);

  return (
    <div
      className={cn(
        "overflow-x-auto border-t border-hairline",
        rows.length > 12 && "max-h-[28rem] overflow-y-auto",
      )}
    >
      <table className="w-full min-w-[36rem] text-sm">
        <thead className="sticky top-0 z-10 bg-panel">
          <tr className="border-b border-hairline text-left">
            <th className="label-micro px-5 py-2 font-normal">Month</th>
            <th className="label-micro px-3 py-2 text-right font-normal">Cashback</th>
            <th className="label-micro px-3 py-2 text-right font-normal">Taken back</th>
            <th className="label-micro px-3 py-2 text-right font-normal">Eligible spend</th>
            <th className="label-micro px-5 py-2 text-right font-normal">Rate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {rows.map((month) => {
            const isOpen = month.month === selected;
            const earning = month.cards.filter(
              (c) => Number(c.real_cashback) !== 0 || Number(c.eligible_spend) > 0,
            );
            return (
              <MonthRow
                key={month.month}
                month={month}
                cards={earning}
                isOpen={isOpen}
                onToggle={() => onSelect(isOpen ? null : month.month)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MonthRow({
  month,
  cards,
  isOpen,
  onToggle,
}: {
  month: CashbackMonth;
  cards: CashbackMonth["cards"];
  isOpen: boolean;
  onToggle: () => void;
}) {
  const value = Number(month.real_cashback);

  return (
    <>
      <tr className={cn(isOpen && "bg-raised/40")}>
        <td className="px-5 py-2.5">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            className={cn(
              "-mx-1 flex items-center gap-1.5 rounded px-1 py-0.5 text-left",
              "transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
              isOpen ? "text-ink" : "text-muted",
            )}
          >
            <ChevronRight
              className={cn("h-3.5 w-3.5 shrink-0 transition-transform", isOpen && "rotate-90")}
              aria-hidden
            />
            <span className="whitespace-nowrap">{formatMonth(month.month)}</span>
            {month.month === currentMonth() && (
              <span className="text-xs text-faint">· so far</span>
            )}
            {month.entry_count > 0 && (
              <span className="text-xs text-faint">
                · {month.entry_count} credit{month.entry_count === 1 ? "" : "s"}
              </span>
            )}
          </button>
        </td>
        <td
          className={cn(
            "whitespace-nowrap px-3 py-2.5 text-right font-medium tabular-nums",
            value > 0 ? "text-brass" : value < 0 ? "text-overdue" : "text-faint",
          )}
        >
          {formatMoney(month.real_cashback)}
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
          {Number(month.clawbacks) > 0 ? (
            <span className="text-warn">−{formatMoney(month.clawbacks)}</span>
          ) : (
            <span className="text-faint">—</span>
          )}
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-muted">
          {formatMoney(month.eligible_spend)}
        </td>
        <td className="whitespace-nowrap px-5 py-2.5 text-right tabular-nums">
          {month.effective_rate === null ? (
            <span
              className={month.timing_sensitive ? "text-warn" : "text-faint"}
              title={
                month.timing_sensitive
                  ? "Every credit this month came from a card that pays a cycle in arrears, so a rate against this month's spend would pair unrelated periods."
                  : undefined
              }
            >
              —
            </span>
          ) : (
            <span className="text-ink">
              {month.effective_rate}%
              {month.timing_sensitive && (
                <span
                  className="text-warn"
                  title="Part of this month's cashback came from a card that pays a cycle in arrears."
                >
                  *
                </span>
              )}
            </span>
          )}
        </td>
      </tr>

      {isOpen && (
        <tr className="bg-raised/20">
          <td colSpan={5} className="px-5 py-3">
            {cards.length === 0 ? (
              <p className="text-xs text-faint">
                Nothing was spent or earned in {formatMonth(month.month)}.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {cards.map((card) => (
                  <li
                    key={card.card_id}
                    className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-xs"
                  >
                    <span
                      className="min-w-0 truncate text-muted"
                      title={card.nickname || card.product_name || card.issuer}
                    >
                      {card.nickname || card.product_name || card.issuer}
                      <span className="ml-1.5 text-faint">•••• {card.last4}</span>
                    </span>
                    <span className="flex shrink-0 items-baseline gap-4 tabular-nums">
                      <span className="text-faint">
                        {formatMoney(card.eligible_spend)} spend
                      </span>
                      {/* No rate for a card paying a cycle in arrears — the
                          reason stands where the number would have been, so it
                          reads as withheld rather than missing. */}
                      {card.credit_timing === "cycle_credit" ? (
                        <span
                          className="inline-flex items-center gap-1 text-warn"
                          title="This card pays one cashback credit per statement cycle, for the cycle before it. Its credit this month was earned on last month's spend, so cashback ÷ this month's spend is not a rate."
                        >
                          <Clock className="h-3 w-3" aria-hidden />
                          earned a cycle earlier
                        </span>
                      ) : (
                        card.effective_rate && (
                          <span className="text-faint">{card.effective_rate}%</span>
                        )
                      )}
                      <span
                        className={cn(
                          "w-24 text-right font-medium",
                          Number(card.real_cashback) > 0
                            ? "text-brass"
                            : Number(card.real_cashback) < 0
                              ? "text-overdue"
                              : "text-faint",
                        )}
                      >
                        {formatMoney(card.real_cashback)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
